import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Modal, ModalHeader, ModalBody, Row, Col } from 'reactstrap';
import FormSelectField from '../../common/components/formFields/FormSelectField';
import FormInputField from '../../common/components/formFields/FormInputField';
import CommonButton from '../../common/components/CommonButton';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import SettingsSectionHeader from '../../settings/SettingsSectionHeader';
import { DamageTypes } from '../../constants/DamageTypes';
import { sendDamagePhoto } from '../AddVehicleActions';
import DamagePhotoUpload from './DamagePhotoUpload';
import { resizeImage } from '../../common/utils/image/Image.utils';
import '../../assets/css/componentStyles/ConditionDetailsModal.css';

const validationSchema = Yup.object().shape({
  damageType: Yup.object()
    .shape({
      value: Yup.string().required(),
      label: Yup.string().required(),
    })
    .required('Damage type is required'),
  description: Yup.string().max(150, 'Description must be 150 characters or less'),
  photos: Yup.array()
    .min(1, 'At least one photo is required')
    .required('At least one photo is required'),
});

function ConditionDetailsModal({
  isOpen,
  toggle,
  onSave,
  damage,
  title = 'Add Condition Inspection Details',
  maxPhotos = 3,
  size = 'lg',
  className = '',
  photoUploadErrorMessage = 'Photo upload failed. Please try again.',
  damagesIndex,
}) {
  const dispatch = useDispatch();
  const [uploadingPhotos, setUploadingPhotos] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});

  const getInitialDamageType = useCallback(() => {
    if (!damage?.damageType) return null;

    if (typeof damage.damageType === 'object' && damage.damageType.value) {
      return damage.damageType;
    }

    const normalized =
      typeof damage.damageType === 'string' ? damage.damageType.toLowerCase() : '';

    const found = DamageTypes.find((type) => type.value.toLowerCase() === normalized);
    return found || null;
  }, [damage?.damageType]);

  const initialValues = useMemo(
    () => ({
      damageType: getInitialDamageType(),
      description: damage?.description || '',
      photos: damage?.photos || [],
    }),
    [getInitialDamageType, damage?.description, damage?.photos],
  );

  const handlePhotoDrop = useCallback(
    async (file, index, setFieldValue, values) => {
      setUploadingPhotos((prev) => ({ ...prev, [index]: true }));
      setUploadErrors((prev) => ({ ...prev, [index]: null }));
      try {
        const resizedImage = await resizeImage(file);

        const result = await dispatch(sendDamagePhoto(resizedImage, values.photos, index));
        const newPhotos = [...values.photos];
        if (result) {
          newPhotos[index] = result;
          setFieldValue('photos', newPhotos);
        } else {
          throw new Error('Photo upload failed: No data returned');
        }
      } catch (error) {
        console.error(photoUploadErrorMessage, error);
        setUploadErrors((prev) => ({
          ...prev,
          [index]: error.message || photoUploadErrorMessage,
        }));
      } finally {
        setUploadingPhotos((prev) => ({ ...prev, [index]: false }));
      }
    },
    [dispatch, photoUploadErrorMessage],
  );

  const handlePhotoDelete = useCallback((index, setFieldValue, values) => {
    const newPhotos = [...values.photos];
    newPhotos.splice(index, 1);
    setFieldValue('photos', newPhotos);
    setUploadErrors((prev) => ({ ...prev, [index]: null }));
  }, []);

  const handlePhotoRetry = useCallback((index) => {
    setUploadErrors((prev) => ({ ...prev, [index]: null }));
  }, []);

  const photoSlots = useMemo(() => [...Array(maxPhotos)], [maxPhotos]);

  const modalContent = useMemo(
    () => ({
      title,
      damageTypeLabel: 'Damage Type',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Please clearly detail all and any damage.',
      photoInstructions: `Please upload up to ${maxPhotos} photos of the damage.`,
      saveButtonLabel: 'Save',
      cancelButtonLabel: 'Cancel',
    }),
    [title, maxPhotos],
  );

  const handleSubmit = useCallback(
    (values, { resetForm }) => {
      const processedValues = {
        ...values,
        damageType: values.damageType?.value || values.damageType,
      };

      onSave({ ...damage, ...processedValues });
      resetForm();
      toggle();
    },
    [damage, onSave, toggle],
  );
  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size={size}
      backdropClassName="condition-details-modal-backdrop"
      className={`condition-details-modal ${className}`.trim()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="condition-details-modal-title"
      aria-describedby="condition-details-modal-description"
    >
      <ModalHeader toggle={toggle} tag="div">
        <SettingsSectionHeader
          id="condition-details-modal-title"
          header={modalContent.title}
          variant="noBorder"
          description="Please clearly detail all and any damage."
          className="conditionModalTitle"
        />
      </ModalHeader>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ dirty, isValid, values, setFieldValue, isSubmitting, errors, submitCount }) => (
          <Form className="mt-4">
            <ModalBody id="condition-details-modal-description">
              <div className="condition-details-modal-description-container">
                <div className="condition-details-modal-description-container-index">
                  <span>{damagesIndex + 1}</span>
                </div>

                <FormSelectField
                  name="damageType"
                  label={modalContent.damageTypeLabel}
                  options={DamageTypes}
                  required
                  aria-describedby="damageType-help"
                  labelVariant="bordered"
                  rowClassNames="w-100"
                  selectProps={{ isSearchable: false }}
                />
              </div>
              <FormInputField
                name="description"
                label={modalContent.descriptionLabel}
                maxLength={150}
                aria-describedby="description-help"
                labelVariant="bordered"
                lengthCounter
              />
              <div className="form-group form-group--bordered-label">
                <label className="form-input-field-label label-required">Photos</label>
                <Row
                  role="group"
                  aria-labelledby="photo-instructions"
                  className="gap-3 gap-md-0"
                >
                  {photoSlots.map((_, index) => {
                    const photo = values.photos[index];
                    const isUploading = !!uploadingPhotos[index];
                    const uploadError = uploadErrors[index];
                    const key = photo?.id || `new-photo-${index}`;
                    return (
                      <Col md={4} key={key}>
                        <DamagePhotoUpload
                          photo={photo}
                          onPhotoDrop={(file) =>
                            handlePhotoDrop(file, index, setFieldValue, values)
                          }
                          onPhotoDelete={() => handlePhotoDelete(index, setFieldValue, values)}
                          onRetry={() => handlePhotoRetry(index, setFieldValue, values)}
                          isUploading={isUploading}
                          uploadError={uploadError}
                          aria-label={`Photo upload slot ${index + 1}`}
                        />
                      </Col>
                    );
                  })}
                </Row>
                {errors.photos && submitCount > 0 && (
                  <label
                    className="form-input-field__label error-label"
                    role="alert"
                    aria-live="polite"
                  >
                    {errors.photos}
                  </label>
                )}
              </div>
              <Row className="mt-4 mb-3 gap-3 gap-md-0">
                <Col md={6}>
                  <CommonButton
                    label={modalContent.saveButtonLabel}
                    variant={CommonButtonVariants.PRIMARY_RED}
                    type="submit"
                    disabled={isSubmitting || !isValid || !dirty}
                    className="w-100"
                    aria-describedby="save-button-help"
                  />
                </Col>
                <Col md={6}>
                  <CommonButton
                    label={modalContent.cancelButtonLabel}
                    variant={CommonButtonVariants.OUTLINED_RED}
                    handleClick={toggle}
                    className="w-100"
                    aria-label="Cancel and close modal"
                    type="button"
                  />
                </Col>
              </Row>
            </ModalBody>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

ConditionDetailsModal.propTypes = {
  /** Whether the modal is open */
  isOpen: PropTypes.bool.isRequired,
  /** Function to toggle modal visibility */
  toggle: PropTypes.func.isRequired,
  /** Function called when saving condition details */
  onSave: PropTypes.func.isRequired,
  /** Existing damage data for editing */
  damage: PropTypes.shape({
    damageType: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ]),
    description: PropTypes.string,
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        url: PropTypes.string,
      }),
    ),
  }),
  title: PropTypes.string,
  maxPhotos: PropTypes.number,
  size: PropTypes.oneOf(['sm', 'lg', 'xl']),
  className: PropTypes.string,
  photoUploadErrorMessage: PropTypes.string,
  damagesIndex: PropTypes.number,
};

ConditionDetailsModal.defaultProps = {
  damage: null,
  title: 'Add Condition Inspection Details',
  maxPhotos: 3,
  size: 'lg',
  className: '',
  photoUploadErrorMessage: 'Photo upload failed. Please try again.',
  damagesIndex: 0,
};

export default ConditionDetailsModal;
